import Chart from 'react-apexcharts'
import { ApexOptions } from 'apexcharts'
import { webApiService } from '../DashboardIndex'
import { useDashboard } from '../context/DashboardContext'
import { useEffect, useState } from 'react'
import { Select, SelectItem, Spinner } from '@nextui-org/react'
import { HiOutlineArrowsExpand, HiOutlineClock, HiOutlineDeviceMobile } from 'react-icons/hi'
import { HiOutlineArrowsPointingIn } from 'react-icons/hi2'
import { COLORS, FUNNEL_COLORS } from '../constants'

interface HistoricalItem {
  id: number
  estado: string
  total: number
}

interface Value {
  brands: string[]
  series: number[]
  labels: string[]
  historical: HistoricalItem[]
}

const initialData: Value = {
  brands: [],
  series: [],
  labels: [],
  historical: []
}

export default function ThirdCharts () {
  const [expanded, setExpanded] = useState(false)
  const [selectedBrand, setSelectedBrand] = useState<string>('Todos')
  const { dateRange, selectedSeller, selectedRegion, selectedStore, incrementLoadingCards, decrementLoadingCards } = useDashboard()
  const [loadingTopTen, setLoadingTopTen] = useState<boolean>(true)
  const [loadingFunnel, setLoadingFunnel] = useState<boolean>(true)
  const [value, setValue] = useState<Value>(initialData)

  const getTopTen = async (brand: string) => {
    const obj = {
      start: String(dateRange.start),
      end: String(dateRange.end),
      idSeller: selectedSeller,
      brand: brand !== 'Todos' ? brand : null,
      region: selectedRegion || null,
      store: selectedStore || null
    }

    const response = await webApiService.getTopTen(obj).then(res => res.data)
    return response
  }

  const handleSelectedBrand = async (brand: string) => {
    setLoadingTopTen(true)
    const response = await getTopTen(brand)
    setValue(prev => ({
      ...prev,
      series: response.series,
      labels: response.labels
    }))
    setLoadingTopTen(false)
    setSelectedBrand(brand)
  }

  useEffect(() => {
    let cancelled = false

    const loadData = async () => {
      setLoadingTopTen(true)
      setLoadingFunnel(true)
      incrementLoadingCards()
      setSelectedBrand('Todos')

      const [topTenRes, historicalRes] = await Promise.all([
        getTopTen('Todos'),
        webApiService.getHistorical({ start: String(dateRange.start), end: String(dateRange.end), idSeller: selectedSeller, region: selectedRegion || null, store: selectedStore || null }).then(res => res.data)
      ])

      if (!cancelled) {
        setValue({
          brands: topTenRes.brands,
          series: topTenRes.series,
          labels: topTenRes.labels,
          historical: historicalRes
        })
        setLoadingTopTen(false)
        setLoadingFunnel(false)
      }
      decrementLoadingCards()
    }

    loadData()

    return () => {
      cancelled = true
    }
  }, [dateRange, selectedRegion, selectedStore]) // eslint-disable-line react-hooks/exhaustive-deps

  const labelCount = value.labels.length
  const barColumnWidth = labelCount <= 3 ? '30px' : '60%'

  const barOptions: ApexOptions = {
    chart: {
      type: 'bar',
      toolbar: { show: false },
      fontFamily: 'inherit'
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        columnWidth: barColumnWidth
      }
    },
    colors: COLORS,
    xaxis: {
      categories: value.labels,
      labels: { style: { colors: '#8492a6', fontSize: '11px' }, rotate: -45 }
    },
    yaxis: {
      title: { text: 'Cantidad Recibida', style: { color: '#8492a6', fontWeight: 600 } },
      labels: { style: { colors: '#8492a6' } }
    },
    dataLabels: { enabled: false },
    grid: { borderColor: '#f0f0f0' },
    tooltip: { theme: 'light' }
  }

  const funnelRealValues = value.historical.map(item => item.total)
  const funnelLabels = value.historical.map(item => item.estado)
  const funnelCount = value.historical.length
  const funnelSyntheticSeries = value.historical.map((_, i) => funnelCount - i)

  const funnelOptions: ApexOptions = {
    chart: {
      type: 'bar',
      toolbar: { show: false },
      fontFamily: 'inherit'
    },
    states: {
      hover: { filter: { type: 'none' } },
      active: { filter: { type: 'none' } }
    },
    plotOptions: {
      bar: {
        borderRadius: 2,
        horizontal: true,
        barHeight: '80%',
        distributed: true,
        isFunnel: true
      }
    },
    colors: FUNNEL_COLORS,
    xaxis: {
      categories: funnelLabels,
      labels: { show: false }
    },
    yaxis: {
      labels: { show: false }
    },
    dataLabels: {
      enabled: true,
      formatter: (_val, opt) => {
        const idx = opt?.dataPointIndex ?? 0
        return `${funnelLabels[idx]} (${funnelRealValues[idx]})`
      },
      dropShadow: { enabled: false },
      style: { fontSize: '13px', fontWeight: 600, colors: ['#fff'] }
    },
    legend: {
      show: true,
      position: 'bottom',
      fontSize: '12px',
      labels: { colors: '#44526B' },
      markers: { size: 6, shape: 'circle' as const }
    },
    grid: { show: false },
    tooltip: {
      theme: 'light',
      y: {
        formatter: (_val, opts) => String(funnelRealValues[opts?.dataPointIndex ?? 0])
      }
    }
  }

  const selectItems = [
    { label: 'Todos', value: 'Todos' },
    ...value.brands.map(brand => ({ label: brand, value: brand }))
  ]

  return (
    <div className={`grid grid-cols-1 gap-6 ${expanded ? '' : 'lg:grid-cols-2'}`}>
      <div className='bg-white rounded-2xl shadow-sm border border-gray-100 p-6'>
        <div className='flex items-center justify-between mb-4'>
          <div className='flex items-center gap-2'>
            <HiOutlineDeviceMobile size={20} className='text-[#8492a6]' />
            <span className='text-sm font-semibold text-[#8492a6]'>Top 10 Smartphone&apos;s recibidos</span>
          </div>
          <div className='flex items-center gap-3'>
            <Select
              label='Marca'
              selectedKeys={[selectedBrand]}
              onChange={(e) => { if (e.target.value) handleSelectedBrand(e.target.value) }}
              className='w-40'
              size='sm'
              variant='bordered'
              classNames={{ trigger: 'bg-white' }}
            >
              {selectItems.map(item => (
                <SelectItem key={item.value} value={item.value}>
                  {item.label}
                </SelectItem>
              ))}
            </Select>
            <button onClick={() => setExpanded(!expanded)} className='text-[#8492a6] hover:text-[#44526B] transition-colors hidden'>
              {expanded ? <HiOutlineArrowsPointingIn size={18} /> : <HiOutlineArrowsExpand size={18} />}
            </button>
          </div>
        </div>
        {loadingTopTen
          ? <div className='flex items-center justify-center h-[300px]'><Spinner size='lg' color='primary' /></div>
          : value.labels.length === 0
            ? <div className='flex items-center justify-center h-[300px] text-sm text-[#8492a6]'>Sin datos disponibles</div>
            : <Chart options={barOptions} series={[{ name: 'Cantidad', data: value.series }]} type='bar' height={expanded ? 400 : 300} />}
      </div>
      <div className='bg-white rounded-2xl shadow-sm border border-gray-100 p-6'>
        <div className='flex items-center gap-2 mb-4'>
          <div className='flex items-center gap-2 text-sm font-semibold text-[#8492a6]'>
            <HiOutlineClock size={18} />
            Histórico de Cotizaciones a Entrega
          </div>
        </div>
        {loadingFunnel
          ? <div className='flex items-center justify-center h-[300px]'><Spinner size='lg' color='primary' /></div>
          : value.historical.length === 0
            ? <div className='flex items-center justify-center h-[300px] text-sm text-[#8492a6]'>Sin datos disponibles</div>
            : <Chart options={funnelOptions} series={[{ name: 'Total', data: funnelSyntheticSeries }]} type='bar' height={expanded ? 400 : 350} />}
      </div>
    </div>
  )
}
